import React, { useState, useEffect } from 'react';
import {
  PlusIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { Card, CardHeader, CardTitle, CardContent } from '../UI/Card';
import { Button } from '../UI/Button';
import LoadingSpinner from '../UI/LoadingSpinner';
import { useTickets } from '../../hooks/useTickets';
import { TicketStatus, TicketPriority, TicketCategory } from '../../types/ticket';
import { formatDate } from '../../utils/i18nFormat';
import { useAuth } from '../../contexts/AuthContext';
import { UserRole, usersService, User } from '../../services/users';
import { useTranslation } from 'react-i18next';

interface TicketListProps {
  onViewTicket: (id: number) => void;
  onEditTicket: (id: number) => void;
  onCreateTicket: () => void;
  refreshTrigger?: number;
}

const statusBadge = {
  [TicketStatus.OPEN]: 'badge-primary',
  [TicketStatus.IN_PROGRESS]: 'badge-warning',
  [TicketStatus.WAITING_CLIENT]: 'badge-secondary',
  [TicketStatus.RESOLVED]: 'badge-success',
  [TicketStatus.CLOSED]: 'badge-secondary',
} as const;

const priorityBadge = {
  [TicketPriority.LOW]: 'badge-success',
  [TicketPriority.MEDIUM]: 'badge-warning',
  [TicketPriority.HIGH]: 'badge-danger',
  [TicketPriority.CRITICAL]: 'badge-danger',
} as const;

export const TicketList: React.FC<TicketListProps> = ({
  onViewTicket,
  onEditTicket,
  onCreateTicket,
  refreshTrigger
}) => {
  const { t } = useTranslation();
  const { tickets, loading, error, pagination, fetchTickets, deleteTicket } = useTickets();
  const { user } = useAuth();
  const [filters] = useState({
    status: '',
    priority: '',
    category: '',
    assigned_to_id: '',
    company_id: '',
  });

  const [, setUsers] = useState<User[]>([]);
  const [, setUsersLoading] = useState<boolean>(false);
  const [, setUsersError] = useState<string | null>(null);
  const isAdminUser = user?.role === UserRole.ADMINISTRATOR;
  const [deletingId, setDeletingId] = useState<number | null>(null);

  useEffect(() => {
    fetchTickets({ page: 1, per_page: 10 });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Refetch list after external changes (create/edit) while preserving filters
  useEffect(() => {
    if (refreshTrigger === undefined) return;
    const filterParams = Object.entries(filters).reduce((acc, [key, value]) => {
      if (value) {
        acc[key] = key.includes('_id') ? parseInt(value) : value;
      }
      return acc;
    }, {} as any);
    fetchTickets({ ...filterParams, page: 1, per_page: pagination.per_page });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setUsersLoading(true);
        setUsersError(null);
        const analysts = await usersService.getAnalysts();
        setUsers(analysts || []);
      } catch (err: any) {
        console.error('Erro ao carregar usuários:', err);
        setUsersError(t('tickets.users.errorLoading'));
      } finally {
        setUsersLoading(false);
      }
    };
    fetchUsers();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePageChange = (page: number) => {
    const filterParams = Object.entries(filters).reduce((acc, [key, value]) => {
      if (value) {
        acc[key] = key.includes('_id') ? parseInt(value) : value;
      }
      return acc;
    }, {} as any);

    fetchTickets({ ...filterParams, page, per_page: pagination.per_page });
  };

  const statusLabels = {
    [TicketStatus.OPEN]: t('tickets.status.open'),
    [TicketStatus.IN_PROGRESS]: t('tickets.status.in_progress'),
    [TicketStatus.WAITING_CLIENT]: t('tickets.status.waiting_client'),
    [TicketStatus.RESOLVED]: t('tickets.status.resolved'),
    [TicketStatus.CLOSED]: t('tickets.status.closed'),
  } as const;

  const priorityLabels = {
    [TicketPriority.LOW]: t('tickets.priority.low'),
    [TicketPriority.MEDIUM]: t('tickets.priority.medium'),
    [TicketPriority.HIGH]: t('tickets.priority.high'),
    [TicketPriority.CRITICAL]: t('tickets.priority.critical'),
  } as const;

  const categoryLabels = {
    [TicketCategory.SECURITY_INCIDENT]: t('tickets.category.security_incident'),
    [TicketCategory.FALSE_POSITIVE]: t('tickets.category.false_positive'),
    [TicketCategory.DATA_REQUEST]: t('tickets.category.data_request'),
    [TicketCategory.TECHNICAL_SUPPORT]: t('tickets.category.technical_support'),
    [TicketCategory.FEATURE_REQUEST]: t('tickets.category.feature_request'),
    [TicketCategory.OTHER]: t('tickets.category.other'),
  } as const;

  return (
    <div>
      <Card>
        <CardHeader className='flex flex-row items-center justify-between space-y-0'>
          <CardTitle className='text-lg'>{t('tickets.title')}</CardTitle>
          <Button onClick={onCreateTicket}>
            <PlusIcon className='h-4 w-4 mr-2' />
            {t('tickets.new')}
          </Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className='flex justify-center py-8'>
              <LoadingSpinner size='lg' />
            </div>
          ) : (
            <div className='overflow-x-auto'>
              <table className='min-w-full divide-y divide-gray-200 dark:divide-gray-700'>
                <thead className='bg-gray-50 dark:bg-gray-800/50'>
                  <tr>
                    <th className='px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider'>{t('tickets.table.id')}</th>
                    <th className='px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider'>{t('tickets.table.title')}</th>
                    <th className='px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider'>{t('tickets.table.status')}</th>
                    <th className='px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider'>{t('tickets.table.priority')}</th>
                    <th className='px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider'>{t('tickets.table.category')}</th>
                    <th className='px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider'>{t('tickets.table.assignee')}</th>
                    <th className='px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider'>{t('tickets.table.createdAt')}</th>
                    <th className='px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider'>{t('tickets.table.actions')}</th>
                  </tr>
                </thead>
                <tbody className='divide-y divide-gray-100 dark:divide-gray-800'>
                  {tickets.map(ticket => (
                    <tr key={ticket.id} className='hover:bg-gray-50 dark:hover:bg-gray-800/50'>
                      <td className='px-3 py-2 text-sm text-gray-700 dark:text-gray-300'>{ticket.id}</td>
                      <td className='px-3 py-2 max-w-[360px]'>
                        <p className='text-sm font-medium text-gray-900 dark:text-white'>{ticket.title}</p>
                        <p className='text-xs text-gray-500 dark:text-gray-400 truncate'>
                          {ticket.description.length > 50
                            ? `${ticket.description.substring(0, 50)}...`
                            : ticket.description}
                        </p>
                      </td>
                      <td className='px-3 py-2'>
                        <span className={`badge ${statusBadge[ticket.status]}`}>{statusLabels[ticket.status]}</span>
                      </td>
                      <td className='px-3 py-2'>
                        <span className={`badge ${priorityBadge[ticket.priority]}`}>{priorityLabels[ticket.priority]}</span>
                      </td>
                      <td className='px-3 py-2'>
                        <span className='badge badge-secondary'>{categoryLabels[ticket.category]}</span>
                      </td>
                      <td className='px-3 py-2'>
                        {ticket.assigned_to ? (
                          <>
                            <p className='text-sm font-medium text-gray-900 dark:text-white'>
                              {ticket.assigned_to.full_name || ticket.assigned_to.email}
                            </p>
                            {ticket.assigned_to.full_name && (
                              <p className='text-xs text-gray-500 dark:text-gray-400'>{ticket.assigned_to.email}</p>
                            )}
                          </>
                        ) : (
                          <span className='text-sm text-gray-500 dark:text-gray-400'>—</span>
                        )}
                      </td>
                      <td className='px-3 py-2 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap'>
                        {formatDate(ticket.created_at, { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
                      </td>
                      <td className='px-3 py-2'>
                        <div className='flex items-center gap-1'>
                          <button
                            title={t('tickets.actions.view')}
                            className='p-1.5 rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700'
                            onClick={() => onViewTicket(ticket.id)}
                          >
                            <EyeIcon className='h-4 w-4' />
                          </button>
                          <button
                            title={t('tickets.actions.edit')}
                            className='p-1.5 rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700'
                            onClick={() => onEditTicket(ticket.id)}
                          >
                            <PencilIcon className='h-4 w-4' />
                          </button>
                          {isAdminUser && (
                            <button
                              title={t('tickets.actions.delete')}
                              disabled={deletingId === ticket.id}
                              className='p-1.5 rounded-md text-danger-500 hover:bg-danger-50 disabled:opacity-50 dark:hover:bg-danger-900/30'
                              onClick={async () => {
                                if (!window.confirm(t('tickets.actions.confirmDelete'))) return;
                                setDeletingId(ticket.id);
                                try {
                                  await deleteTicket(ticket.id);
                                } finally {
                                  setDeletingId(null);
                                }
                              }}
                            >
                              <TrashIcon className='h-4 w-4' />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {pagination.total_pages > 1 && (
        <div className='flex justify-center items-center gap-2 mt-6'>
          <button
            onClick={() => handlePageChange(pagination.page - 1)}
            disabled={pagination.page === 1}
            className='p-2 rounded-md border border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed'
          >
            <ChevronLeftIcon className='h-4 w-4' />
          </button>
          <span className='text-sm text-gray-700 dark:text-gray-300'>
            {pagination.page} / {pagination.total_pages}
          </span>
          <button
            onClick={() => handlePageChange(pagination.page + 1)}
            disabled={pagination.page === pagination.total_pages}
            className='p-2 rounded-md border border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed'
          >
            <ChevronRightIcon className='h-4 w-4' />
          </button>
        </div>
      )}

      {tickets.length === 0 && !loading && (
        <div className='text-center py-12'>
          <p className='text-lg text-gray-500 dark:text-gray-400'>{t('tickets.empty')}</p>
          <Button onClick={onCreateTicket} className='mt-4'>
            <PlusIcon className='h-4 w-4 mr-2' />
            {t('tickets.actions.createFirst')}
          </Button>
        </div>
      )}
    </div>
  );
};
